import type { TextReferenceMatch } from './text-references';

/**
 * A bare abbreviated or full commit SHA, as GitHub links it: 7 to 40 lowercase hex characters
 * that are not part of a word, path, domain, UUID, `#` color or `user@sha` reference.
 */
const COMMIT_SHA_PATTERN =
  /(?<![\p{L}\p{N}_@#/\\.-])[\da-f]{7,40}(?![\p{L}\p{N}_@/\\-]|\.[\p{L}\p{N}_])/gu;

export type CommitReferenceOptions = {
  org: string;
  repo: string;
};

export type CommitReferenceMatch = TextReferenceMatch & {
  sha: string;
};

export function buildCommitUrl({ org, repo }: CommitReferenceOptions, sha: string): string {
  return `https://github.com/${encodeURIComponent(org)}/${encodeURIComponent(repo)}/commit/${sha}`;
}

// Undashed UUIDs and MD5 digests are 32 hex characters, a length no one abbreviates a SHA to.
const NON_COMMIT_HEX_LENGTH = 32;

export function findCommitReferences(content: string): CommitReferenceMatch[] {
  return (
    [...content.matchAll(COMMIT_SHA_PATTERN)]
      // GitHub links only SHAs of commits that exist. Without that lookup, a SHA needs a digit and
      // a letter, so numbers such as comment IDs and hex words such as "defaced" stay text.
      .filter(
        ([sha]) => sha.length !== NON_COMMIT_HEX_LENGTH && /\d/.test(sha) && /[a-f]/.test(sha),
      )
      .map((match) => ({
        end: match.index + match[0].length,
        sha: match[0],
        start: match.index,
      }))
  );
}
